import type { AnyAction, Middleware } from "redux";

import {
  characterEnvSelectors,
  characterSelectors,
  Constants,
} from "@dndbeyond/character-rules-engine/es";

import { appEnvSelectors } from "~/tools/js/Shared/selectors";
import type { SharedAppState } from "~/tools/js/Shared/stores/typings";

import { getChangeLocation } from "./helpers/changeLocation";
import { sendCharacterAction, sendClickElement } from "./pipeline";
import type { CharacterActionInput, ClickElementInput } from "./pipeline";

/**
 * Turns a builder action into CHARACTER_ACTION data, or null to skip it.
 * Receives the state before and after the action so deselects can look up
 * values that are removed by the action.
 */
export type CharacterActionMapper<A extends AnyAction = AnyAction> = (
  action: A,
  prevState: SharedAppState,
  nextState: SharedAppState
) => CharacterActionInput | null;

export type CharacterActionMappers = Partial<
  Record<string, CharacterActionMapper<any>>
>;

/**
 * Turns a builder action into CLICK_ELEMENT events. One action can change
 * several settings at once, so it returns a list (empty to skip).
 */
export type ClickElementMapper<A extends AnyAction = AnyAction> = (
  action: A,
  prevState: SharedAppState,
  nextState: SharedAppState
) => ClickElementInput[];

export type ClickElementMappers = Partial<
  Record<string, ClickElementMapper<any>>
>;

const isBuilder = (state: SharedAppState) =>
  characterEnvSelectors.getContext(state) ===
  Constants.AppContextTypeEnum.BUILDER;

// Mirrors the character saga filter, which drops changes to loaded characters
// the user can't edit
const isReadonly = (state: SharedAppState) =>
  !characterSelectors.getCanEdit(state) &&
  characterEnvSelectors.getLoadingStatus(state) ===
    Constants.CharacterLoadingStatusEnum.LOADED;

/**
 * Redux middleware that sends CHARACTER_ACTION and CLICK_ELEMENT events for
 * mapped actions dispatched while the builder is open. Instrumentation
 * failures never affect the dispatch.
 */
export const createInstrumentationMiddleware =
  (
    characterActionMappers: CharacterActionMappers,
    clickElementMappers: ClickElementMappers = {}
  ): Middleware<{}, SharedAppState> =>
  (store) =>
  (next) =>
  (action: AnyAction) => {
    const characterActionMapper = characterActionMappers[action?.type];
    const clickElementMapper = clickElementMappers[action?.type];
    if (!characterActionMapper && !clickElementMapper) return next(action);

    const prevState = store.getState();
    const result = next(action);
    const nextState = store.getState();

    try {
      if (!isBuilder(nextState) || isReadonly(prevState)) return result;

      // Where the action was dispatched from, for example "standard_builder"
      const location = getChangeLocation(
        characterEnvSelectors.getContext(prevState),
        globalThis.location?.pathname ?? ""
      );

      const characterAction = characterActionMapper?.(
        action,
        prevState,
        nextState
      );
      if (characterAction) {
        sendCharacterAction(characterAction, {
          characterId: appEnvSelectors.getCharacterId(nextState),
          location,
        });
      }

      clickElementMapper?.(action, prevState, nextState).forEach((click) => {
        sendClickElement(click, { location });
      });
    } catch {
      // Instrumentation must never break a dispatch
    }

    return result;
  };
